import breadcrumbCollector from "./breadcrumb-collector";
import { SERVER_URL } from "../constants/api";
import cookieStorage from "./cookie-storage";

const ERROR_REPORT_URL = `${SERVER_URL}/api/error-report`;
const BATCH_INTERVAL = 5000;
const MAX_BATCH_SIZE = 10;
const DEDUP_WINDOW = 60000;

class ErrorReporter {
	constructor() {
		this._queue = [];
		this._recentErrors = new Map();
		this._batchTimer = null;
		this._initialized = false;
		this._capturing = false;
	}

	init() {
		if (this._initialized) return;
		this._initialized = true;

		breadcrumbCollector.init();

		window.addEventListener("error", (event) => {
			this.captureError({
				message: event.message,
				stack: event.error?.stack,
				errorType: "UnhandledError",
				severity: "error",
				endpoint: window.location.pathname,
			});
		});

		window.addEventListener("unhandledrejection", (event) => {
			const reason = event.reason;
			this.captureError({
				message: reason?.message || String(reason),
				stack: reason?.stack,
				errorType: "UnhandledRejection",
				severity: "error",
				endpoint: window.location.pathname,
			});
		});

		this._batchTimer = setInterval(() => this._sendBatch(), BATCH_INTERVAL);
		window.addEventListener("beforeunload", () => this._sendBatch(true));
	}

	captureError({ message, stack, errorType, severity = "error", endpoint,
		httpStatus, httpMethod, extra = {} }) {
		// Re-entrancy guard — prevent infinite recursion if captureError itself throws
		if (this._capturing) return;
		this._capturing = true;
		try { this._captureErrorInner({ message, stack, errorType, severity, endpoint, httpStatus, httpMethod, extra }); }
		catch (e) { /* never let error reporting cause an error */ }
		finally { this._capturing = false; }
	}

	_captureErrorInner({ message, stack, errorType, severity, endpoint,
		httpStatus, httpMethod, extra }) {
		// Dedup: skip if same error reported recently
		const fingerprint = `${message}|${errorType}|${endpoint}`;
		const now = Date.now();
		if (this._recentErrors.has(fingerprint)) {
			if (now - this._recentErrors.get(fingerprint) < DEDUP_WINDOW) return;
		}
		this._recentErrors.set(fingerprint, now);

		// Clean up old dedup entries
		if (this._recentErrors.size > 100) {
			for (const [key, ts] of this._recentErrors) {
				if (now - ts > DEDUP_WINDOW) this._recentErrors.delete(key);
			}
		}

		const userData = this._getUserData();
		const streamState = this._getStreamState();

		const errorPayload = {
			message: (message || "").substring(0, 2000),
			stack: (stack || "").substring(0, 5000),
			errorType,
			severity,
			endpoint: endpoint || window.location.pathname,
			httpStatus,
			httpMethod,
			currentPage: window.location.pathname,
			isInLivestream: streamState.isStream || false,
			livestreamChannelName: streamState.channelName,
			deviceInfo: {
				userAgent: navigator.userAgent,
				platform: "web",
				screenWidth: window.innerWidth,
				screenHeight: window.innerHeight,
				language: navigator.language,
				url: window.location.href,
			},
			breadcrumbs: breadcrumbCollector.getBreadcrumbs(),
			appVersion: process.env.REACT_APP_VERSION || "unknown",
		};

		// Spread extra into a nested key to prevent overwriting core fields
		if (extra && Object.keys(extra).length > 0) {
			errorPayload.extra = extra;
		}

		if (userData) {
			errorPayload.userId = userData._id;
			errorPayload.userEmail = userData.email;
		}

		this._queue.push(errorPayload);

		if (this._queue.length >= MAX_BATCH_SIZE) {
			this._sendBatch();
		}
	}

	_sendBatch(sync = false) {
		if (this._queue.length === 0) return;

		const batch = this._queue.splice(0, MAX_BATCH_SIZE);

		for (const errorPayload of batch) {
			try {
				const headers = { "Content-Type": "application/json" };
				const token = cookieStorage.getCookie("Authorization") || localStorage.getItem("token");
				if (token) {
					headers["Authorization"] = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
				}

				if (sync && navigator.sendBeacon) {
					navigator.sendBeacon(
						ERROR_REPORT_URL,
						new Blob([JSON.stringify(errorPayload)], { type: "application/json" })
					);
				} else {
					fetch(ERROR_REPORT_URL, {
						method: "POST",
						headers,
						body: JSON.stringify(errorPayload),
						keepalive: true,
					}).catch(() => {});
				}
			} catch (e) {
				// Never let error reporting cause an error
			}
		}
	}

	_getUserData() {
		try {
			const str = localStorage.getItem("userData");
			if (!str) return null;
			const parsed = JSON.parse(str);
			return parsed?.userData || parsed;
		} catch {
			return null;
		}
	}

	_getStreamState() {
		try {
			const store = window.__SIKI_STORE__;
			if (store) {
				const state = store.getState();
				return {
					isStream: state.stream?.isStream || false,
					channelName: state.stream?.streamData?.channelName,
				};
			}
			return {};
		} catch {
			return {};
		}
	}
}

const errorReporter = new ErrorReporter();
export default errorReporter;
